import type { BoardItem, BoardComment, CreateCommentRequest, StorySummary } from '../types/board.js';

const BOARD_URL = '/api/board';

export interface BoardUser {
  id: number;
  name: string;
  avatar_url: string | null;
}

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    credentials: 'include',
    ...init,
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(body.error?.message ?? body.error ?? `Request failed: ${response.status}`);
  }
  return response.json() as Promise<T>;
}

export async function getBoardItems(): Promise<BoardItem[]> {
  return fetchJson<BoardItem[]>(BOARD_URL);
}

export async function getBoardUsers(): Promise<BoardUser[]> {
  return fetchJson<BoardUser[]>(`${BOARD_URL}/users`);
}

export async function getComments(sourceType: string, sourceId: string, projectId?: string): Promise<BoardComment[]> {
  const params = projectId ? `?projectId=${projectId}` : '';
  return fetchJson<BoardComment[]>(`${BOARD_URL}/items/${sourceType}/${sourceId}/comments${params}`);
}

export async function createComment(data: CreateCommentRequest): Promise<BoardComment> {
  return fetchJson<BoardComment>(
    `${BOARD_URL}/items/${data.source_type}/${data.source_id}/comments`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content: data.content, author: data.author, project_id: data.project_id }),
    },
  );
}

export async function deleteComment(
  sourceType: string,
  sourceId: string,
  commentId: number | string,
  projectId?: string
): Promise<void> {
  const params = projectId ? `?projectId=${projectId}` : '';
  await fetch(`${BOARD_URL}/items/${sourceType}/${sourceId}/comments/${commentId}${params}`, {
    method: 'DELETE',
    credentials: 'include',
  }).then(res => {
    if (!res.ok) return res.json().then(body => { throw new Error(body.error ?? `Request failed: ${res.status}`); });
  });
}

export async function deleteBoardItem(itemId: string): Promise<void> {
  const [sourceType, projectId, sourceId] = itemId.split(':');
  await fetch(`${BOARD_URL}/items/${sourceType}/${projectId}/${sourceId}`, {
    method: 'DELETE',
    credentials: 'include',
  }).then(res => {
    if (!res.ok) return res.json().then(body => { throw new Error(body.error ?? `Request failed: ${res.status}`); });
  });
}

export async function updateBoardItemStatus(itemId: string, boardColumn: string): Promise<void> {
  const [sourceType, projectId, sourceId] = itemId.split(':');
  await fetchJson(`${BOARD_URL}/items/${sourceType}/${projectId}/${sourceId}/status`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ boardColumn }),
  });
}

export async function updateGoalAssignee(goalId: number, assignee: string): Promise<void> {
  await fetchJson(`/api/breakdown/goals/${goalId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ assignee }),
  });
}

export async function updateGoalAgentId(goalId: number, agentId: number | null): Promise<void> {
  await fetchJson(`/api/breakdown/goals/${goalId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ agent_id: agentId }),
  });
}

export interface CreateBacklogItemRequest {
  title: string;
  description?: string;
  priority: string;
  category: 'todo' | 'bug';
  projectId: number;
  skillIds?: string[];
}

export interface CreateBacklogItemResponse {
  itemId: string;
  projectId: number;
  title: string;
}

export async function createBacklogItem(data: CreateBacklogItemRequest): Promise<CreateBacklogItemResponse> {
  return fetchJson<CreateBacklogItemResponse>(`${BOARD_URL}/backlog`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function getSpecStories(projectId: number, specId: string): Promise<StorySummary[]> {
  return fetchJson<StorySummary[]>(`/api/projects/${projectId}/specs/${specId}/stories`);
}

export async function updateStoryModel(projectId: number, specId: string, storyId: string, model: string): Promise<void> {
  await fetchJson(`/api/projects/${projectId}/specs/${encodeURIComponent(specId)}/stories/${encodeURIComponent(storyId)}/model`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ model }),
  });
}

export async function getStoryContent(projectId: number, specId: string, storyFile: string): Promise<string> {
  const result = await fetchJson<{ content: string }>(`/api/projects/${projectId}/specs/${encodeURIComponent(specId)}/story-content?file=${encodeURIComponent(storyFile)}`);
  return result.content;
}

export async function updateTodoLinkedProject(todoId: number, linkedProjectId: number | null): Promise<void> {
  await fetchJson(`/api/todos/${todoId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ linked_project_id: linkedProjectId }),
  });
}

export interface SkillOption {
  name: string;
  description: string;
}

export async function getSkills(): Promise<SkillOption[]> {
  const result = await fetchJson<{ skills?: SkillOption[] }>('/api/breakdown/context?type=daily');
  return result.skills ?? [];
}
